import {
    findMountedOwnerForMediaKey,
    getMediaOwnerControlSnapshot,
    getMediaPlaybackOwner,
    getMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'
import {
    DEFAULT_VIDEO_PLAYBACK_RATES,
    normalizePlaybackRate,
    normalizePlaybackRates,
} from 'lib/videoPlaybackRates'

// What the cinema transport's settings menu shows for the dock's own runtime:
// its speed (and the speeds it offers — a YouTube player reports its own
// list) and whether it can show captions and is. Read from the dock's own
// owner when it holds the track — a paused runtime must not be mistaken for
// the post's still-mounted copy — else from the mounted owner the
// coordinator knows for the track; before any runtime is up, the track's
// remembered speed stands in so the menu never opens on a blank choice.

/**
 * @typedef {{
 *   playbackRate: number,
 *   playbackRates: number[],
 *   captionsAvailable: boolean,
 *   captionsEnabled: boolean,
 * }} StageRuntimeControls
 */

/** @type {StageRuntimeControls} */
export const EMPTY_STAGE_RUNTIME_CONTROLS = {
    playbackRate: 1,
    playbackRates: DEFAULT_VIDEO_PLAYBACK_RATES,
    captionsAvailable: false,
    captionsEnabled: false,
}

/**
 * The runtime the stage reads: the dock's own owner while it holds the
 * track, else whichever mounted owner the coordinator finds for it.
 */
export function findStageRuntimeOwner(mediaKey, ownerId = null) {
    if (!mediaKey) return null
    const own = ownerId ? getMediaPlaybackOwner(ownerId) : null
    if (own && own.mediaKey === mediaKey) return own
    return findMountedOwnerForMediaKey(mediaKey)
}

/**
 * @param {string | null | undefined} mediaKey
 * @param {{ ownerId?: string | null }} [options]
 * @returns {StageRuntimeControls}
 */
export function readStageRuntimeControls(mediaKey, options = {}) {
    if (!mediaKey) return EMPTY_STAGE_RUNTIME_CONTROLS

    const owner = findStageRuntimeOwner(mediaKey, options.ownerId || null)
    const control = owner ? getMediaOwnerControlSnapshot(owner) : null
    const stored = getMediaPlaybackState(mediaKey)
    const playbackRate =
        normalizePlaybackRate(control?.playbackRate) ??
        normalizePlaybackRate(stored?.playbackRate) ??
        1

    return {
        playbackRate,
        playbackRates: normalizePlaybackRates(control?.playbackRates, playbackRate),
        captionsAvailable: control?.captionsAvailable === true,
        captionsEnabled: control?.captionsEnabled === true,
    }
}

export function stageRuntimeControlsEqual(first, second) {
    if (first === second) return true
    if (!first || !second) return false
    return (
        Math.abs(first.playbackRate - second.playbackRate) < 0.001 &&
        first.captionsAvailable === second.captionsAvailable &&
        first.captionsEnabled === second.captionsEnabled &&
        first.playbackRates.length === second.playbackRates.length &&
        first.playbackRates.every(
            (rate, index) => Math.abs(rate - second.playbackRates[index]) < 0.001,
        )
    )
}
